import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress, Grid, Typography } from '@material-ui/core'
import React, { useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import { requiredAddressFieldValues } from '../../../lib/cart/address'
import { defaultBillingAddressValues } from '../../../lib/cart/billing'
import { useDefaultCountryCode } from '../../../lib/cart/hooks/useDefaultCountryCode'
import { isBillingSameAsShipping } from '../../../lib/cart/shipping'
import { CustomerAddressFromQuery, FormValues, ValidFormValues } from '../../../lib/cart/types'
import { assertRequiredFields } from '../../../utils/assertTools'
import { logAndCaptureException } from '../../../utils/errorTools'
import ApplicationErrorMessage from '../../ApplicationErrorMessage'
import ErrorUpdatingCartMessage from '../../ErrorUpdatingCartMessage'
import { GridButton } from '../shared/GridButton'
import BillingDetails from './BillingDetails'

export interface NoPaymentDetailsRequiredProps {
  cart: CartFragment
  className?: string
  defaultBillingAddress?: CustomerAddressFromQuery
  error?: Error
  onSubmit: (values: ValidFormValues.NoPaymentDetailsRequired) => void
  submitting: boolean
}

const NoPaymentDetailsRequired: React.FC<NoPaymentDetailsRequiredProps> = ({
  cart,
  className,
  defaultBillingAddress,
  error,
  onSubmit,
  submitting,
}) => {
  const {
    billing_address: billingAddress,
    shipping_addresses: [shippingAddress],
  } = cart
  const defaultCountryCode = useDefaultCountryCode()
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(
    () => isBillingSameAsShipping(cart) ?? false,
  )

  const defaultAddressValues = useMemo(
    () =>
      defaultBillingAddressValues({
        billingAddress,
        billingSameAsShipping,
        defaultBillingAddress,
        defaultCountryCode,
        shippingAddress,
      }),
    [
      billingAddress,
      billingSameAsShipping,
      defaultBillingAddress,
      defaultCountryCode,
      shippingAddress,
    ],
  )

  const methods = useForm<FormValues.NoPaymentDetailsRequired>({
    mode: 'onBlur',
    defaultValues: defaultAddressValues,
  })

  const formErrorsAlertRef = useRef<HTMLDivElement>(null)
  const [runTypeError, setRunTypeError] = useState(false)

  const hasFormErrors = useMemo(
    () => Object.keys(methods.formState.errors).length > 0,
    [methods.formState],
  )

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault()
    }
    setRunTypeError(false)

    const isValid = await methods.trigger()

    if (!isValid) {
      const formErrorsEl = formErrorsAlertRef.current
      if (formErrorsEl) {
        formErrorsEl.scrollIntoView({ behavior: 'smooth' })
      }
      return
    }

    const formValues = methods.getValues()

    try {
      assertRequiredFields(formValues, requiredAddressFieldValues)
    } catch (err) {
      logAndCaptureException(err)
      setRunTypeError(true)
      return
    }

    onSubmit({
      billingSameAsShipping,
      code: 'free' as const,
      ...formValues,
    })
  }

  return (
    <Grid
      alignItems="center"
      className={className}
      justifyContent="center"
      container
      item
      xs={12}
      component="form"
      noValidate
      onSubmit={handleSubmit}
    >
      <Grid item xs={12}>
        <Typography variant="h5">PAYMENT METHOD</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography className="noPaymentRequiredText" variant="body2" gutterBottom>
          No payment required on zero balance after rebates.
        </Typography>
      </Grid>
      <Grid ref={formErrorsAlertRef} item xs={12}>
        {hasFormErrors && (
          <Alert type="error">Please check the highlighted fields for errors.</Alert>
        )}
        {error && (
          <Alert type="error">
            <ErrorUpdatingCartMessage />
          </Alert>
        )}
        {runTypeError && (
          <Alert type="error">
            <ApplicationErrorMessage />
          </Alert>
        )}
      </Grid>
      <FormProvider {...methods}>
        <BillingDetails
          billingSameAsShipping={billingSameAsShipping}
          defaultAddressValues={defaultAddressValues}
          setBillingSameAsShipping={setBillingSameAsShipping}
        />
      </FormProvider>
      <GridButton
        onClick={handleSubmit}
        type="submit"
        id="continue-to-review-button"
        data-cy="go-to-review"
      >
        {submitting ? <CircularProgress size={24} color="inherit" /> : 'CONTINUE TO REVIEW'}
      </GridButton>
    </Grid>
  )
}

const StyledNoPaymentDetailsRequired = styled(NoPaymentDetailsRequired)({
  '.noPaymentRequiredText': {
    marginBottom: '40px',
  },
})

export default StyledNoPaymentDetailsRequired
